// dependencies
import { format } from "date-fns";
import React, { PropsWithChildren, useState } from "react";
import { useDispatch } from "react-redux";

import { Spinner, toast } from "@performyard/py-components";

import { AdminContainer } from "../../../components/Admin";
import Button from "../../../components/Button";
import { Grid, GridCol } from "../../../components/Grid";
import Toggle from "../../../components/Inputs/ToggleSwitches/ThumbSwitch";
import PageBanner from "../../../components/PageBanner";
import Spacer from "../../../components/Spacer";
import Typography from "../../../components/Text";

import { useUpdateTeamsAutoSyncSettings } from "../../../api/hooks";

import { postTeamsManualSync } from "../../../api/services";

import createSocket from "../../../utils/Socket";

import css from "./Teams.module.scss";

interface TeamsUserNotificationSettingsProps {
	adminConsent: {
		hasAdminConsent: boolean;
		hasAdminConsentV2: boolean;
	};
	automaticSyncEnabled: boolean;
	numConnectedUsers: number;
	lastSyncDate: Date;
	refetchTeamsIntegrationData: () => void;
}

export const TeamsUserNotificationSettings = (props: PropsWithChildren<TeamsUserNotificationSettingsProps>) => {
	const {
		automaticSyncEnabled,
		numConnectedUsers = 0,
		lastSyncDate = null,
		adminConsent,
		refetchTeamsIntegrationData = () => {},
	} = props;
	const { hasAdminConsentV2 } = adminConsent;
	const [manualResyncInProgress, setJobInProgress] = useState(false);

	const dispatch = useDispatch();

	const updateAutomaticSync = useUpdateTeamsAutoSyncSettings().mutate;

	const formatLastSyncDate = (date: Date) => {
		// format the date like `Apr 20, 2023 · 10:01pm`
		return format(date, "MMM d, yyyy • h:mma");
	};

	const onToggleAutomaticSync = (enabled: boolean) => {
		updateAutomaticSync({
			proactive_install: enabled ? "auto" : "manual",
		});
	};

	const manualSync = async () => {
		setJobInProgress(true);
		await postTeamsManualSync(dispatch);
		const socket = createSocket("/api/async_jobs/get_job_status/?name=teams-manual-user-installation");
		try {
			socket.on("message", (message) => {
				if (message.data.completed !== true) {
					setJobInProgress(true);
				} else {
					setJobInProgress(false);
					socket.close();
					refetchTeamsIntegrationData();
					toast.success("Successfully Synced Users");
				}
			});
		} catch {
			setJobInProgress(false);
			socket.close();
			refetchTeamsIntegrationData();
			toast.error("Error Syncing Users");
		}
	};

	if (hasAdminConsentV2 === false) {
		return (
			<AdminContainer>
				<PageBanner
					size="large"
					type={"info"}
					title="To enable this feature for MS Teams, please authorize the PerformYard App by clicking the button
					below"
					description="Please note that you must be a Microsoft Teams Administrator to complete this step"
					isDismissible={false}
					actionButtonText="Begin Authorization"
					actionButtonHref="/microsoft_teams/get-admin-consent/?"
				/>
			</AdminContainer>
		);
	}

	return (
		<div>
			<Grid cols={8}>
				<GridCol span={3}>
					<Typography size="paragraph-2" weight="semi-bold" color="neutral-9">
						Notifications
					</Typography>
					<Typography size="paragraph-3" weight="light" color="neutral-9">
						Microsoft Teams notification will send your Performyard notifications directly to your Microsoft
						Teams account.
					</Typography>
				</GridCol>

				<GridCol span={1}>{/* Just spacing */}</GridCol>

				<GridCol span={4}>
					<Grid cols={4}>
						<GridCol span={2} className={css.settingsCol}>
							<Typography size="paragraph-2" weight="semi-bold" color="neutral-9">
								Automatic Sync
							</Typography>
							<Typography size="paragraph-3" weight="light" color="neutral-9">
								Automatically installs PerformYard App in Microsoft Teams for new PerformYard users.
							</Typography>
							{lastSyncDate ? (
								<div className={css.detailsBox}>
									<Typography size="caption" weight="semi-bold" color="neutral-9">
										Last Sync: {formatLastSyncDate(lastSyncDate)}
									</Typography>
								</div>
							) : null}
						</GridCol>
						<GridCol span={1}>{/* Just spacing */}</GridCol>
						<GridCol span={1}>
							<div className={`${css.rightAligned}`}>
								<Toggle
									value={automaticSyncEnabled}
									onChange={(val) => {
										onToggleAutomaticSync(val);
									}}
								/>
							</div>
						</GridCol>
					</Grid>

					<Spacer height={4} />

					<Grid cols={4}>
						<GridCol span={2} className={css.settingsCol}>
							<Typography size="paragraph-2" weight="semi-bold" color="neutral-9">
								Manual Sync
							</Typography>
							<Typography size="paragraph-3" weight="light" color="neutral-9">
								This action will install the PerformyYard App in Microsoft Teams for new users who have
								logged into PerformYard.
							</Typography>
						</GridCol>
						<GridCol span={1}>{/* Just spacing */}</GridCol>
						<GridCol span={1}>
							<div className={`${css.rightAligned}`}>
								{manualResyncInProgress ? (
									<div className={css.spinner}>
										<Spinner size={20} />
									</div>
								) : (
									<Button onClick={manualSync}>Sync Users</Button>
								)}
							</div>
						</GridCol>
					</Grid>

					<Spacer height={4} />

					<Grid cols={4}>
						<GridCol span={3}>
							<Typography size="paragraph-3" weight="semi-bold" color="neutral-9">
								Number of users connected to Microsoft Teams:
							</Typography>
						</GridCol>
						<GridCol span={1}>
							<div className={`${css.rightAligned}`}>
								<Typography size="paragraph-2" weight="semi-bold" color="neutral-9">
									{numConnectedUsers}
								</Typography>
							</div>
						</GridCol>
					</Grid>
				</GridCol>
			</Grid>
		</div>
	);
};
